import { gql, useMutation, useQuery } from '@apollo/client';
import logger from '@orn/shared-web/lib/logger';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import formatBytes from '@orn/shared-web/utils/formatBytes';
import * as React from 'react';

import Button from '../../components/Button';
import DangerConfirmDialog from '../../components/DangerConfirmDialog';
import Header from '../../components/Header';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';

import { buildStaticAssetKey, contentTypeForFile, uploadFileToPresignedUrl } from './staticAssetFiles';

import type {
  AdminCreateStaticAssetUploadMutation,
  AdminCreateStaticAssetUploadMutationVariables,
  AdminDeleteStaticAssetMutation,
  AdminDeleteStaticAssetMutationVariables,
  AdminListStaticAssetsQuery,
} from './__generated__/StaticAssetsScene.gql';

const ADMIN_LIST_STATIC_ASSETS = gql`
  query AdminListStaticAssets {
    adminListStaticAssets {
      key
      url
      sizeBytes
      lastModifiedAt
    }
  }
`;

const ADMIN_CREATE_STATIC_ASSET_UPLOAD = gql`
  mutation AdminCreateStaticAssetUpload($key: String!, $contentType: String!) {
    adminCreateStaticAssetUpload(key: $key, contentType: $contentType) {
      key
      url
      uploadUrl
    }
  }
`;

const ADMIN_DELETE_STATIC_ASSET = gql`
  mutation AdminDeleteStaticAsset($key: String!) {
    adminDeleteStaticAsset(key: $key)
  }
`;

/**
 * Static assets tool: lists the marketing static-assets bucket (the store caps
 * the walk at 5000 objects) with each object's public URL, uploads new files,
 * and deletes. Uploads never pass through mops-api — it mints a presigned PUT and
 * the browser sends the bytes straight to S3, so file size is not bounded by
 * the Lambda payload limit.
 */
const StaticAssetsScene = () => {
  const [isUploading, setIsUploading] = React.useState(false);
  const [keyToDelete, setKeyToDelete] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const { data, loading, error, refetch } = useQuery<AdminListStaticAssetsQuery>(ADMIN_LIST_STATIC_ASSETS);
  const [createUpload] = useMutation<
    AdminCreateStaticAssetUploadMutation,
    AdminCreateStaticAssetUploadMutationVariables
  >(ADMIN_CREATE_STATIC_ASSET_UPLOAD);
  const [deleteAsset] = useMutation<AdminDeleteStaticAssetMutation, AdminDeleteStaticAssetMutationVariables>(
    ADMIN_DELETE_STATIC_ASSET
  );

  const assets = React.useMemo(() => data?.adminListStaticAssets ?? [], [data]);
  const existingKeys = React.useMemo(() => new Set(assets.map((asset) => asset.key)), [assets]);

  const uploadOne = async (file: File): Promise<boolean> => {
    const key = buildStaticAssetKey(file.name);
    if (!key) {
      showToast({ type: 'error', message: `Skipped "${file.name}": nothing usable left in the name.` });
      return false;
    }
    const contentType = contentTypeForFile(file);
    const wasReplacing = existingKeys.has(key);
    try {
      const result = await createUpload({ variables: { key, contentType } });
      const upload = result.data?.adminCreateStaticAssetUpload;
      if (!upload) {
        throw new Error('No upload URL returned');
      }
      try {
        await uploadFileToPresignedUrl(upload.uploadUrl, file, contentType);
      } catch (error) {
        // The direct-to-S3 PUT bypasses Apollo, so the errorLink never sees it.
        logger.error('Static asset upload to S3 failed', error, { tags: { key } });
        throw error;
      }
      showToast({ type: 'success', message: `${wasReplacing ? 'Replaced' : 'Uploaded'} ${upload.url}` });
      return true;
    } catch {
      showToast({ type: 'error', message: `Failed to upload ${file.name}.` });
      return false;
    }
  };

  const handleFilesChosen = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setIsUploading(true);
    let uploadedCount = 0;
    for (const file of Array.from(files)) {
      if (await uploadOne(file)) {
        uploadedCount += 1;
      }
    }
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (uploadedCount > 0) {
      await refetch();
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast({ type: 'success', message: 'URL copied.' });
    } catch (error) {
      logger.error('Clipboard write failed', error);
      showToast({ type: 'error', message: 'Could not copy — select the URL and copy it by hand.' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!keyToDelete) {
      return;
    }
    await deleteAsset({ variables: { key: keyToDelete } });
    showToast({ type: 'success', message: `Deleted ${keyToDelete}. The CDN drops it within a few minutes.` });
    setKeyToDelete(null);
    await refetch();
  };

  const heading = `All assets · ${assets.length}`;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Static assets</h1>

        <div className="border-border-primary flex items-center gap-3 border-b pb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            aria-label="Choose files to upload"
            onChange={(event) => void handleFilesChosen(event.target.files)}
          />
          <Button color="primary" size="sm" isLoading={isUploading} onClick={() => fileInputRef.current?.click()}>
            Upload files…
          </Button>
          <span className="text-fg-tertiary text-xs">Spaces in file names become dashes.</span>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">{heading}</h2>

          {loading ? (
            <span className="text-fg-secondary text-xs">Loading…</span>
          ) : error ? (
            <div className="flex items-center gap-3">
              <span className="text-fg-error-primary text-xs">Could not list the bucket: {error.message}</span>
              <Button color="secondary" size="xs" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : assets.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">
              Nothing on the CDN yet. Upload something and it is live in seconds.
            </span>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Key</Table.HeaderCell>
                  <Table.HeaderCell>Size</Table.HeaderCell>
                  <Table.HeaderCell>Modified</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {assets.map((asset) => (
                  <Table.Row key={asset.key}>
                    <Table.Cell mono>
                      <a
                        href={asset.url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-fg-brand-primary focus-visible:outline-border-brand underline-offset-2 hover:underline focus-visible:outline-2"
                      >
                        {asset.key}
                      </a>
                    </Table.Cell>
                    <Table.Cell muted>{formatBytes(asset.sizeBytes)}</Table.Cell>
                    <Table.Cell muted>{formatTimeAgo(asset.lastModifiedAt)}</Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end gap-2">
                        <Button color="secondary" size="xs" onClick={() => void handleCopyUrl(asset.url)}>
                          Copy URL
                        </Button>
                        <Button color="danger" size="xs" onClick={() => setKeyToDelete(asset.key)}>
                          Delete
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>

        <DangerConfirmDialog
          isOpen={keyToDelete !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setKeyToDelete(null);
            }
          }}
          title="Delete static asset"
          body={
            <p className="text-fg-primary text-sm">
              <span className="font-mono">{keyToDelete}</span> stops being served once the CDN cache expires. Anything
              on the marketing site that links to it will break.
            </p>
          }
          confirmLabel="Delete"
          onConfirm={handleDeleteConfirm}
        />
      </main>
    </div>
  );
};

export default StaticAssetsScene;
